import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { FaCheck } from 'react-icons/fa';
import JSConfetti from 'js-confetti';
import {
  ModalOverlay,
  ModalContent,
  Form,
  FormTitle,
  FormSubTitle,
  FlexRow,
  InputWrapper,
  InputField,
  SubmitButton,
  CloseButton
} from './ContactModal.styles';

const ContactModal = ({ isOpen, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [number, setNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const confettiRef = useRef(null);
  const { t } = useTranslation();
  
  // Initialize confetti
  useEffect(() => {
    if (isOpen && !confettiRef.current) {
      confettiRef.current = new JSConfetti();
    }
    
    return () => {
      confettiRef.current = null;
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const confirm = async (e) => {
    e.preventDefault();
    
    const formData = new FormData(e.target);
    setIsSubmitting(true);
    
    try {
      const response = await fetch("https://n8n.matandessaur.me/webhook/lead-form", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        // Show personalized thank you toast with custom styling
        toast.success(
          `${t('formText.thankYou')} ${name}! ${t('formText.responseMessage')}`,
          {
            duration: 5000,
            position: 'top-center',
            style: {
              background: '#4CAF50',
              color: 'white',
              fontWeight: '500',
              padding: '16px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderRadius: '8px',
            },
            icon: <FaCheck style={{ height: '35px', width: '35px', color: 'white' }} />,
          }
        );
        
        // Launch confetti celebration
        if (confettiRef.current) {
          confettiRef.current.addConfetti({
            confettiColors: ['#4CAF50', '#FFFFFF', '#FFD700', '#FF6347', '#00BFFF'],
            confettiRadius: 6,
            confettiNumber: 300,
          });
        }
        
        // Clear form fields
        setName('');
        setEmail('');
        setNumber('');
        
        onClose();
      } else {
        toast.error("There was an error submitting the form.");
      }
    } catch (error) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContent>
        <div>
          <FormTitle>
            {t('formText.schedulingAppointment')}
          </FormTitle>
          <FormSubTitle>
            {t('formText.schedulingSlogan')} <span>⚡</span>
          </FormSubTitle>
        </div>
        <Form onSubmit={confirm} id="form">
          {/* Removing web3forms access key as we're using n8n webhook now */}
          <input type="hidden" name="subject" value="Hurray we got a new client 🎊" />
          <input type="hidden" name="from_name" value="ontheweb request" />
          <input type="checkbox" name="botcheck" className="hidden" style={{ display: "none" }} />
          <FlexRow>
            <InputWrapper>
              <label htmlFor="contact-name" className="sr-only">{t('formText.fullname')}</label>
              <InputField
                id="contact-name"
                type="text"
                name="name"
                required
                value={name}
                placeholder={t('formText.fullname')}
                aria-label={t('formText.fullname')}
                onChange={(e) => setName(e.target.value)}
              />
            </InputWrapper>
            <InputWrapper>
              <label htmlFor="contact-phone" className="sr-only">{t('formText.phone')}</label>
              <InputField
                id="contact-phone"
                type="tel"
                name="phone"
                required
                value={number}
                placeholder={t('formText.phone')}
                aria-label={t('formText.phone')}
                onChange={(e) => setNumber(e.target.value)}
              />
            </InputWrapper>
          </FlexRow>
          <InputWrapper>
            <label htmlFor="contact-email" className="sr-only">{t('formText.email')}</label>
            <InputField
              id="contact-email"
              type="email"
              name="email"
              required
              value={email}
              placeholder={t('formText.email')}
              aria-label={t('formText.email')}
              onChange={(e) => setEmail(e.target.value)}
            />
          </InputWrapper>
          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('formText.submitting') : t('formText.submit')}
          </SubmitButton>
        </Form>
        <CloseButton onClick={onClose} aria-label="Close contact form" title="Close">
          <svg
            fill="none"
            stroke="var(--text-color)"
            height="16px"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path d="M17.25 17.25 6.75 6.75"></path>
            <path d="m17.25 6.75-10.5 10.5"></path>
          </svg>
        </CloseButton>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ContactModal;